import React from 'react';
import { User } from "@/types";
import { EmployeeTable } from "../EmployeeTable";
import { Skeleton } from "@/components/ui/skeleton";
import { FileQuestion } from "lucide-react";
import { EmployeeModal } from "../EmployeeModal";
import { UserRole } from "@/types/enums";

interface EmployeesListProps {
  users: User[];
  isLoading: boolean;
  canCreateUsers?: boolean;
  isNewUserDialogOpen?: boolean;
  setIsNewUserDialogOpen?: (open: boolean) => void;
  addEmployee?: (employeeData: Partial<Omit<User, 'id'>> & { email: string; name: string; role: string }, shiftAssignment?: {
    shiftId: string;
    fromDate: string;
    toDate?: string;
  }) => Promise<void>;
  handleDepartmentChange: (userId: string, newDepartment: string) => void;
  onViewProfile: (employee: User) => void;
  onEdit: (employee: User) => void;
  onViewOvertimeHistory: (employee: User) => void;
  onChangeStatus: (employee: User) => void;
}

export function EmployeesList({
  users,
  isLoading,
  canCreateUsers,
  isNewUserDialogOpen,
  setIsNewUserDialogOpen,
  addEmployee,
  handleDepartmentChange,
  onViewProfile,
  onEdit,
  onViewOvertimeHistory,
  onChangeStatus
}: EmployeesListProps) {
  if (isLoading && users.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  
  if (users.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileQuestion className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold mb-2">No employees found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          You haven't added any employees yet. 
          {canCreateUsers && addEmployee && setIsNewUserDialogOpen && typeof isNewUserDialogOpen !== 'undefined' 
            ? "Click the \"Add New Employee\" button to get started."
            : "Please contact an administrator to add employees."}
        </p>
        {canCreateUsers && addEmployee && setIsNewUserDialogOpen && typeof isNewUserDialogOpen !== 'undefined' && (
          <EmployeeModal
            mode="add"
            open={isNewUserDialogOpen}
            onOpenChange={setIsNewUserDialogOpen}
            onSave={(employeeData, shiftAssignment) => {
              // Transform EmployeeFormData to the expected addEmployee format
              const transformedData = {
                name: employeeData.name,
                email: employeeData.email,
                department: employeeData.department,
                position: employeeData.position,
                residency: employeeData.residency,
                workerType: employeeData.workerType,
                employee_id: employeeData.employee_id,
                role: UserRole.EMPLOYEE // Default role for new employees
              };
              return addEmployee?.(transformedData, shiftAssignment);
            }}
          />
        )}
      </div>
    );
  }
  
  return (
    <EmployeeTable
      // ✅ NEW: Pass data and loading state to EmployeeTable
      data={users}
      isLoading={isLoading}
      handleDepartmentChange={handleDepartmentChange}
      viewEmployeeProfile={onViewProfile}
      handleEditUser={onEdit}
      viewOvertimeHistory={onViewOvertimeHistory}
      openChangeStatusDialog={onChangeStatus}
      // Pass read-only flag to hide action buttons
      isReadOnly={!canCreateUsers}
    />
  );
}
